/**
 * Уведомления в открытом клиенте: системное уведомление, когда вкладка не в фокусе,
 * звук нового сообщения и счётчик упоминаний в заголовке вкладки. Кому что показывать,
 * решается так же, как у worker'а push: ЛС — всегда, кроме заглушённых бесед, сервер — по уровню
 * уведомлений канала/сервера и упоминаниям; заглушённые каналы и категории молчат.
 */
import { appTitle } from '@/lib/stage'
import { isMobileNow } from './mobile'
import { useAppStore, type View } from '@/store/app'
import { useGuildStore, channelMuted } from '@/store/guilds'
import { DM_SETTINGS, ownMuted, messageNotificationLevel, messageMentionsUser } from '@/lib/muting'
import { useSocialStore, channelName, displayName } from '@/store/social'
import { usePresenceStore } from '@/store/presence'
import { isEphemeral } from '@/lib/interactions'
import { ChannelType, type Message } from '@/types/api'

export interface NotifyPrefs {
  /** Системные уведомления, когда окно не в фокусе. */
  desktop: boolean
  /** Звук нового сообщения. */
  sound: boolean
  /** Web Push при закрытой вкладке (подписка в браузере). */
  push: boolean
}

const KEY = 'klichat.notifications'
const DEFAULTS: NotifyPrefs = { desktop: true, sound: true, push: false }

export function readPrefs(): NotifyPrefs {
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? { ...DEFAULTS, ...(JSON.parse(raw) as Partial<NotifyPrefs>) } : DEFAULTS
  } catch {
    return DEFAULTS
  }
}

export function writePrefs(p: NotifyPrefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p))
  } catch {
    /* приватный режим */
  }
}

const NOTIFY_ALL = 0
const NOTIFY_MENTIONS = 1
const NOTIFY_NOTHING = 2

/** Открыт ли канал сообщения на экране прямо сейчас. */
export function isViewing(view: View, msg: Message): boolean {
  if (view.kind === 'home') return 'channelId' in view && view.channelId === msg.channel_id && !(isMobileNow() && useAppStore.getState().dmSearch?.channelId === msg.channel_id)
  if (view.kind !== 'guild' || view.channelId !== msg.channel_id) return false
  const channel = useGuildStore.getState().channels[view.guildId]?.[msg.channel_id]
  const app = useAppStore.getState()
  return channel?.type !== ChannelType.GuildVoice || (app.voiceChatChannelId === msg.channel_id && !app.searchQuery)
}

/** Нужно ли уведомлять о сообщении (без учёта фокуса окна). */
export function wantsNotification(msg: Message): boolean {
  const me = useAppStore.getState().currentUser?.id
  if (!me || msg.author.id === me || useSocialStore.getState().relationships?.[msg.author.id]?.type === 2) return false
  if (msg.type !== 0 && msg.type !== 19) return false
  // «только вам» — ответ бота на своё же действие, звать к нему незачем
  if (isEphemeral(msg) || (msg.flags ?? 0) & (128 | 4096)) return false
  const guilds = useGuildStore.getState()
  // ЛС — всегда, кроме заглушённых бесед
  if (!msg.guild_id) return !ownMuted(guilds.settings[DM_SETTINGS], msg.channel_id)
  const guild = guilds.guilds[msg.guild_id]
  const settings = guilds.settings[msg.guild_id]
  if (!guild || channelMuted(settings, msg.channel_id, guilds.channels[msg.guild_id])) return false
  const level = messageNotificationLevel(settings, guilds.channels[msg.guild_id]?.[msg.channel_id], guilds.channels[msg.guild_id], guild.default_message_notifications)
  if (level === NOTIFY_ALL) return true
  if (level === NOTIFY_NOTHING || level !== NOTIFY_MENTIONS) return false
  return messageMentionsUser(msg, me, settings, guilds.members[msg.guild_id]?.[me]?.roles ?? [])
}

/** Заголовок и текст уведомления — как у push с сервера. */
export function describe(msg: Message): { title: string; body: string } {
  const author = msg.member?.nick || displayName(msg.author)
  let title = author
  if (msg.guild_id) {
    const g = useGuildStore.getState()
    const ch = g.channels[msg.guild_id]?.[msg.channel_id]
    title = `${author} · #${ch?.name ?? 'канал'} · ${g.guilds[msg.guild_id]?.name ?? 'сервер'}`
  } else {
    const ch = useSocialStore.getState().channels[msg.channel_id]
    if (ch && ch.type === 3) title = `${author} · ${channelName(ch)}`
  }
  let body = msg.content.trim()
  if (body) {
    const names = new Map(msg.mentions.map((u) => [u.id, '@' + displayName(u)]))
    body = body
      .replace(/<@!?(\d+)>/g, (_, id: string) => names.get(id) ?? '@участник')
      .replace(/<@&\d+>/g, '@роль')
      .replace(/<#\d+>/g, '#канал')
      .replace(/\s+/g, ' ')
    if (body.length > 200) body = body.slice(0, 199) + '…'
  }
  const files = msg.attachments?.length ?? 0
  if (msg.poll) body = `📊 Опрос: ${msg.poll.question.text ?? ''}`
  else if ((msg.flags ?? 0) & 8192) body = '🎤 Голосовое сообщение'
  else if (msg.message_snapshots?.length) body = `↪ ${msg.message_snapshots[0]?.message.content.trim().slice(0, 200) || 'Пересланное сообщение'}`
  else if (body && files) body += ` 📎 ${files}`
  else if (!body && files === 1) body = `📎 ${msg.attachments[0]?.filename ?? 'файл'}`
  else if (!body && files > 1) body = `📎 ${files} файла(ов)`
  else if (!body) body = 'Новое сообщение'
  return { title, body }
}

let audio: AudioContext | null = null

/** Короткий двухтональный сигнал без аудиофайла. */
export function playSound() {
  try {
    audio ??= new AudioContext()
    const ctx = audio
    if (ctx.state === 'suspended') void ctx.resume()
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.0001, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.32)
    gain.connect(ctx.destination)
    for (const [freq, at] of [
      [660, 0],
      [880, 0.12],
    ] as const) {
      const osc = ctx.createOscillator()
      osc.type = 'sine'
      osc.frequency.value = freq
      osc.connect(gain)
      osc.start(ctx.currentTime + at)
      osc.stop(ctx.currentTime + at + 0.2)
    }
  } catch {
    /* автовоспроизведение запрещено до первого клика */
  }
}

/** Путь канала для клика по уведомлению: /channels/@me/{id} или /channels/{guild}/{id}. */
export function channelPath(msg: Message): string {
  return msg.guild_id ? `/channels/${msg.guild_id}/${msg.channel_id}` : `/channels/@me/${msg.channel_id}`
}

/** Открывает канал по пути из уведомления (service worker или системное уведомление). */
export function openPath(path: string) {
  const app = useAppStore.getState()
  const dm = /^\/channels\/@me\/(\d+)/.exec(path)
  if (dm?.[1]) {
    app.setView({ kind: 'home', channelId: dm[1] })
    return
  }
  const guild = /^\/channels\/(\d+)\/(\d+)/.exec(path)
  if (guild?.[1] && guild[2]) {
    app.openGuild(guild[1], guild[2])
    if (useGuildStore.getState().channels[guild[1]]?.[guild[2]]?.type === ChannelType.GuildVoice) {
      app.setVoiceChatChannelId(guild[2])
      app.setSearchQuery(null)
    }
  }
}

/**
 * Реакция на MESSAGE_CREATE из шлюза: звук и системное уведомление, если окно не в фокусе.
 * «Не беспокоить» глушит и то и другое; счётчики упоминаний считаются как обычно — их ведёт стор.
 */
export function onMessageCreate(msg: Message) {
  if (usePresenceStore.getState().status === 'dnd') return
  if (!wantsNotification(msg)) return
  const prefs = readPrefs()
  const focused = document.visibilityState === 'visible' && document.hasFocus()
  const viewing = isViewing(useAppStore.getState().view, msg)
  if (focused && viewing) return
  if (prefs.sound) playSound()
  if (!prefs.desktop || focused || typeof Notification === 'undefined' || Notification.permission !== 'granted') return
  const { title, body } = describe(msg)
  try {
    const n = new Notification(title, { body, icon: '/icon-192.png', tag: `channel:${msg.channel_id}`, silent: true })
    n.onclick = () => {
      window.focus()
      openPath(channelPath(msg))
      n.close()
    }
  } catch {
    /* браузер без конструктора Notification в окне (Android) — там работает service worker */
  }
}

/** Счётчик упоминаний в заголовке вкладки. */
export function setTitleBadge(count: number) {
  document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${appTitle}` : appTitle
}
